import React, { useEffect, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import productsService from '../../service/api/ProductsService';
import FormErrorMessageScrollerRhf from '../_shared/FormErrorMessageScrollerRhf';
import { useTranslation } from 'react-i18next';
import { Product, Unit } from '../../types/Product';
import WriteOnlyButton from '../controls/WriteOnlyButton';
import enumService from '../../service/EnumService';
import { DropdownField, NumberField, TextField } from '../_shared/fields';

interface FormValuesProps extends Product {}

const INIT_FORM_STATE: FormValuesProps = {
  id: null,
  name: '',
  description: '',
  targetQuantity: null,
  unit: null,
  densityGMl: null,
  tareWeightG: null,
};

type Props = {
  dialogVisible: boolean;
  closeAddEditModal: VoidFunction;
  product: Product;

  showAddToast: (name: string) => void;
  showSaveToast: (name: string) => void;

  readOnly?: boolean;
};

function ProductDialog({
  dialogVisible,
  closeAddEditModal,
  product,

  showAddToast,
  showSaveToast,

  readOnly,
}: Props) {
  const { t } = useTranslation();

  const methods = useForm<FormValuesProps>({
    defaultValues: INIT_FORM_STATE,
  });

  const { handleSubmit, reset, setError, watch } = methods;

  const selectedUnit = watch('unit');

  const hideDialog = () => {
    reset(INIT_FORM_STATE);
    closeAddEditModal();
  };

  const units = useMemo(() => enumService.getTranslatedEnum(t, Unit, 'products.units'), [t]);

  const editMode = !!product;
  useEffect(() => {
    if (dialogVisible && editMode) {
      reset({
        ...product,
      });
    } else {
      reset(INIT_FORM_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogVisible]);

  const onSubmit = async (formData: FormValuesProps) => {
    try {
      if (!formData.id) {
        await productsService.create(formData);

        showAddToast(formData.name);
      } else {
        await productsService.edit(formData, formData.id);

        showSaveToast(formData.name);
      }

      hideDialog();
    } catch (error: any) {
      if (error.response.status === 409) {
        const field = error.response.data.field;
        setError(field, { type: 'manual', message: t(`products.error_${field}_already_taken`) });
      }
    }
  };

  const DialogFooter = (
    <>
      <Button
        type="reset"
        label={t('common.cancel')}
        icon="pi pi-times"
        className="p-button-text"
        onClick={hideDialog}
      />

      <WriteOnlyButton
        disabled={readOnly ? true : undefined}
        type="submit"
        label={t('common.save')}
        icon="pi pi-check"
        className="p-button-text"
        onClick={handleSubmit(onSubmit)}
      />
    </>
  );

  return (
    <Dialog
      visible={dialogVisible}
      header={t('products.product_details')}
      modal
      className="p-fluid"
      footer={DialogFooter}
      onHide={hideDialog}
      breakpoints={{ '1400px': '60vw', '896px': '90vw' }}
      style={{ width: '40vw' }}
    >
      <FormProvider {...methods}>
        <FormErrorMessageScrollerRhf>
          <div className="col-10">
            <TextField
              containerClass=""
              inputName={`name`}
              inputLabel={t('products.name')}
              disabled={readOnly ? true : undefined}
              rules={{
                validate: (value) => {
                  if (!value?.trim()) {
                    return t('products.error_name_required');
                  }
                  return true;
                },
              }}
            />

            <TextField
              containerClass=""
              inputName={`description`}
              inputLabel={t('products.description')}
              disabled={readOnly ? true : undefined}
            />

            <NumberField
              containerClass=""
              inputName={`targetQuantity`}
              inputLabel={t('products.target_quantity')}
              disabled={readOnly ? true : undefined}
              maxFractionDigits={5}
              rules={{
                validate: (value) => {
                  if (value == null) {
                    return t('products.error_target_quantity_required');
                  }
                  return true;
                },
              }}
            />

            <DropdownField
              containerClass=""
              inputName={`unit`}
              inputLabel={t('products.unit')}
              disabled={readOnly ? true : undefined}
              options={units}
              placeholder={t('common.select')}
              rules={{
                validate: (value) => {
                  if (value == null) {
                    return t('products.error_unit_required');
                  }
                  return true;
                },
              }}
            />

            {(selectedUnit === Unit.ML || selectedUnit === Unit.L) && (
              <NumberField
                containerClass=""
                inputName={`densityGMl`}
                inputLabel={t('products.density_g_ml')}
                disabled={readOnly ? true : undefined}
                maxFractionDigits={5}
              />
            )}

            {/* <NumberField
              containerClass=""
              inputName={`tareWeightG`}
              inputLabel={t('products.tare_weight_g')}
              disabled={readOnly ? true : undefined}
              maxFractionDigits={5}
            /> */}
          </div>
        </FormErrorMessageScrollerRhf>
      </FormProvider>
    </Dialog>
  );
}

export default ProductDialog;
